/**
 * Creator-side Delegation Settings
 * Where creators toggle which actions their Talent Manager can perform on their behalf.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sidebar } from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { Switch } from '../components/ui/switch';
import { Button } from '../components/ui/button';
import { ShieldCheck, Save } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PERMISSION_FIELDS = [
  { key: 'can_accept_campaigns', label: 'Accept / decline campaigns', desc: 'TM can respond to brand pitches on your behalf.' },
  { key: 'can_submit_deliverables', label: 'Submit deliverables', desc: 'TM can upload draft links and final posted URLs.' },
  { key: 'can_generate_invoice', label: 'Generate invoices', desc: 'TM can create and send invoices on your behalf.' },
  { key: 'can_edit_rates', label: 'Edit rate card', desc: 'TM can change your pricing across Reel / Story / UGC / etc.' },
  { key: 'can_edit_media_kit', label: 'Edit media kit', desc: 'TM can upload / replace your media kit files.' },
  { key: 'can_manage_availability', label: 'Manage availability', desc: 'TM can mark you Available / Booked / Vacation, set blocked dates.' },
  { key: 'can_reply_to_brands', label: 'Reply to brand threads', desc: 'TM can post in approval threads with the brand.' },
];

export const CreatorDelegation = () => {
  const { user } = useAuth();
  const [perms, setPerms] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/talent/creators/${user.id}/permissions`, { withCredentials: true });
        setPerms(data.permissions || {});
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [user?.id]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/talent/creators/${user.id}/permissions`, { permissions: perms }, { withCredentials: true });
      toast.success('Delegation settings updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex min-h-screen"><Sidebar /><div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]"><div className="text-center py-20"><div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#FF451A] border-r-transparent"></div></div></div></div>;
  }

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]" data-testid="creator-delegation-page">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF451A] mb-2 flex items-center gap-2"><ShieldCheck size={12} /> Delegation</div>
            <h1 className="font-display text-5xl tracking-tight text-[#0F0F0F] dark:text-white mb-2">Permissions.</h1>
            <p className="text-[#6B6B6B] dark:text-zinc-400">Control exactly what your Talent Manager can do on your behalf. Off = TM cannot take that action.</p>
          </div>

          <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl divide-y divide-white/5">
            {PERMISSION_FIELDS.map((f) => (
              <div key={f.key} className="p-5 flex items-start gap-4" data-testid={`perm-row-${f.key}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-[#0F0F0F] dark:text-white font-medium">{f.label}</div>
                  <div className="text-sm text-[#6B6B6B] dark:text-zinc-500 mt-0.5">{f.desc}</div>
                </div>
                <Switch
                  checked={perms[f.key] !== false}
                  onCheckedChange={(v) => setPerms({ ...perms, [f.key]: v })}
                  data-testid={`perm-toggle-${f.key}`}
                />
              </div>
            ))}
          </div>

          <div className="mt-6 flex items-center justify-between">
            <p className="text-xs text-[#6B6B6B] dark:text-zinc-500">🔒 Your Talent Manager will see these toggles as enforced gates on their actions.</p>
            <Button onClick={save} disabled={saving} className="bg-[#FF451A] hover:bg-[#E63E17] text-[#0F0F0F] dark:text-white rounded-full px-6" data-testid="save-perms-button">
              <Save size={14} className="mr-1" /> {saving ? 'Saving…' : 'Save Permissions'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
